import { DeviceType } from '@/constants/device';

export interface DeviceDownloadData {
  wallpaperId: string;
  deviceType: DeviceType;
  resolution: string;
  isOptimal: boolean;
  timestamp: string;
  userAgent?: string;
  screenDimensions?: {
    width: number;
    height: number;
  };
}

export interface DownloadAnalytics {
  totalDownloads: number;
  deviceBreakdown: Record<DeviceType, number>;
  optimalDownloads: number;
  popularResolutions: Record<string, number>;
}

class DownloadTrackingService {
  private readonly STORAGE_KEY = 'wallpaper_downloads';
  private readonly MAX_STORED_DOWNLOADS = 100;

  /**
   * Tracks a device-specific download
   */
  trackDeviceDownload(downloadData: Omit<DeviceDownloadData, 'timestamp' | 'userAgent' | 'screenDimensions'>): void {
    const enrichedData: DeviceDownloadData = {
      ...downloadData,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      screenDimensions: {
        width: window.screen.width,
        height: window.screen.height
      }
    };

    // Store locally for offline tracking
    this.storeDownloadLocally(enrichedData);

    // Send to analytics service (in a real app)
    this.sendToAnalytics(enrichedData);

    // Log for debugging
    console.log('Device-specific download tracked:', enrichedData);
  }

  /**
   * Gets download analytics for a specific wallpaper
   */
  getWallpaperAnalytics(wallpaperId: string): DownloadAnalytics {
    const downloads = this.getStoredDownloads().filter(d => d.wallpaperId === wallpaperId);
    
    return this.calculateAnalytics(downloads);
  }

  /**
   * Gets overall download analytics
   */
  getOverallAnalytics(): DownloadAnalytics {
    const downloads = this.getStoredDownloads();
    return this.calculateAnalytics(downloads);
  }

  /**
   * Gets device preference trends
   */
  getDevicePreferenceTrends(): {
    mostPopularDevice: DeviceType;
    deviceUsagePercentage: Record<DeviceType, number>;
    optimalChoiceRate: number;
  } {
    const downloads = this.getStoredDownloads();
    const analytics = this.calculateAnalytics(downloads);
    
    const totalDownloads = analytics.totalDownloads;
    if (totalDownloads === 0) {
      return {
        mostPopularDevice: DeviceType.MOBILE_PHONE,
        deviceUsagePercentage: {
          [DeviceType.MOBILE_PHONE]: 0,
          [DeviceType.TABLET]: 0,
          [DeviceType.DESKTOP]: 0,
          [DeviceType.ULTRAWIDE]: 0
        },
        optimalChoiceRate: 0
      };
    }

    // Find most popular device
    const mostPopularDevice = Object.entries(analytics.deviceBreakdown)
      .reduce((a, b) => a[1] > b[1] ? a : b)[0] as DeviceType;

    // Calculate usage percentages
    const deviceUsagePercentage: Record<DeviceType, number> = {
      [DeviceType.MOBILE_PHONE]: (analytics.deviceBreakdown[DeviceType.MOBILE_PHONE] || 0) / totalDownloads * 100,
      [DeviceType.TABLET]: (analytics.deviceBreakdown[DeviceType.TABLET] || 0) / totalDownloads * 100,
      [DeviceType.DESKTOP]: (analytics.deviceBreakdown[DeviceType.DESKTOP] || 0) / totalDownloads * 100,
      [DeviceType.ULTRAWIDE]: (analytics.deviceBreakdown[DeviceType.ULTRAWIDE] || 0) / totalDownloads * 100
    };

    // Calculate optimal choice rate
    const optimalChoiceRate = (analytics.optimalDownloads / totalDownloads) * 100;

    return {
      mostPopularDevice,
      deviceUsagePercentage,
      optimalChoiceRate
    };
  }

  /**
   * Clears stored download data
   */
  clearDownloadData(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to clear download data:', error);
    }
  }

  /**
   * Exports download data for analysis
   */
  exportDownloadData(): DeviceDownloadData[] {
    return this.getStoredDownloads();
  }

  private storeDownloadLocally(downloadData: DeviceDownloadData): void {
    try {
      const existingData = this.getStoredDownloads();
      existingData.push(downloadData);

      // Keep only the most recent downloads to prevent storage bloat
      if (existingData.length > this.MAX_STORED_DOWNLOADS) {
        existingData.splice(0, existingData.length - this.MAX_STORED_DOWNLOADS);
      }

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(existingData));
    } catch (error) {
      console.warn('Failed to store download tracking data:', error);
    }
  }

  private getStoredDownloads(): DeviceDownloadData[] {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.warn('Failed to retrieve download data:', error);
      return [];
    }
  }

  private sendToAnalytics(downloadData: DeviceDownloadData): void {
    // In a real implementation, this would send to your analytics service
    // Example implementations:
    
    // Google Analytics 4
    // gtag('event', 'wallpaper_download', {
    //   wallpaper_id: downloadData.wallpaperId,
    //   device_type: downloadData.deviceType,
    //   resolution: downloadData.resolution,
    //   is_optimal: downloadData.isOptimal
    // });

    // Custom analytics endpoint
    // fetch('/api/analytics/download', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify(downloadData)
    // }).catch(error => console.warn('Analytics tracking failed:', error));

    // Firebase Analytics
    // import { logEvent } from 'firebase/analytics';
    // logEvent(analytics, 'wallpaper_download', {
    //   wallpaper_id: downloadData.wallpaperId,
    //   device_type: downloadData.deviceType,
    //   resolution: downloadData.resolution,
    //   is_optimal: downloadData.isOptimal
    // });

    console.log('Analytics data would be sent:', downloadData);
  }

  private calculateAnalytics(downloads: DeviceDownloadData[]): DownloadAnalytics {
    const analytics: DownloadAnalytics = {
      totalDownloads: downloads.length,
      deviceBreakdown: {
        [DeviceType.MOBILE_PHONE]: 0,
        [DeviceType.TABLET]: 0,
        [DeviceType.DESKTOP]: 0,
        [DeviceType.ULTRAWIDE]: 0
      },
      optimalDownloads: 0,
      popularResolutions: {}
    };

    downloads.forEach(download => {
      // Count device types
      analytics.deviceBreakdown[download.deviceType]++;

      // Count optimal downloads
      if (download.isOptimal) {
        analytics.optimalDownloads++;
      }

      // Count popular resolutions
      if (analytics.popularResolutions[download.resolution]) {
        analytics.popularResolutions[download.resolution]++;
      } else {
        analytics.popularResolutions[download.resolution] = 1;
      }
    });

    return analytics;
  }
}

export const downloadTrackingService = new DownloadTrackingService();